<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import PipelineStageAccordion from './PipelineStageAccordion.vue';

const props = defineProps({
  pipeline: { type: Object, default: null },
  pipelines: { type: Array, default: () => [] },
});

const emit = defineEmits(['move', 'deleteDeals']);

const { t } = useI18n();

const dialogRef = ref(null);
const action = ref('move');
const targetStageId = ref(null);
const confirmingDelete = ref(false);

// Funis de destino possíveis (qualquer funil que não seja o que está sendo excluído).
const targetPipelines = computed(() =>
  props.pipelines.filter(p => p.id !== props.pipeline?.id)
);

const dealsCount = computed(() => props.pipeline?.dealsCount ?? 0);

const canMove = computed(() => targetPipelines.value.length > 0);

const isConfirmDisabled = computed(() => {
  if (action.value === 'move') return !targetStageId.value;
  return false;
});

const confirmLabel = computed(() => {
  if (action.value === 'delete') {
    return confirmingDelete.value
      ? t('PIPELINE_SETTINGS.DELETE_DIALOG.CONFIRM_DELETE_DEALS')
      : t('PIPELINE_SETTINGS.DELETE_DIALOG.DELETE_DEALS');
  }
  return t('PIPELINE_SETTINGS.DELETE_DIALOG.MOVE_CONFIRM');
});

const reset = () => {
  action.value = canMove.value ? 'move' : 'delete';
  targetStageId.value = null;
  confirmingDelete.value = false;
};

watch(action, () => {
  confirmingDelete.value = false;
});

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const handleConfirm = () => {
  if (action.value === 'move') {
    emit('move', { targetStageId: targetStageId.value });
    return;
  }
  // Excluir é destrutivo: exige uma segunda confirmação explícita.
  if (!confirmingDelete.value) {
    confirmingDelete.value = true;
    return;
  }
  emit('deleteDeals');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      t('PIPELINE_SETTINGS.DELETE_DIALOG.TITLE', { name: pipeline?.name })
    "
    :description="
      t('PIPELINE_SETTINGS.DELETE_DIALOG.DESCRIPTION', { count: dealsCount })
    "
    :confirm-button-label="confirmLabel"
    :disable-confirm-button="isConfirmDisabled"
    width="lg"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-3">
      <RadioCard
        v-if="canMove"
        id="pipeline-delete-move"
        :label="t('PIPELINE_SETTINGS.DELETE_DIALOG.MOVE_LABEL')"
        :description="t('PIPELINE_SETTINGS.DELETE_DIALOG.MOVE_DESCRIPTION')"
        :is-active="action === 'move'"
        @select="action = 'move'"
      />
      <div v-if="action === 'move' && canMove" class="px-1">
        <PipelineStageAccordion
          v-model="targetStageId"
          :pipelines="targetPipelines"
          :placeholder="t('PIPELINE_SETTINGS.DELETE_DIALOG.TARGET_PLACEHOLDER')"
        />
      </div>

      <RadioCard
        id="pipeline-delete-deals"
        :label="t('PIPELINE_SETTINGS.DELETE_DIALOG.DELETE_LABEL')"
        :description="t('PIPELINE_SETTINGS.DELETE_DIALOG.DELETE_DESCRIPTION')"
        :is-active="action === 'delete'"
        @select="action = 'delete'"
      />

      <p
        v-if="action === 'delete' && confirmingDelete"
        class="text-sm text-n-ruby-11 font-medium px-1"
      >
        {{
          t('PIPELINE_SETTINGS.DELETE_DIALOG.DELETE_WARNING', {
            count: dealsCount,
          })
        }}
      </p>
    </div>
  </Dialog>
</template>
